import React, { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import {
  Typography,
  Divider,
  Box,
  CircularProgress,
  Paper,
} from '@mui/material';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { ServicePlan } from '../../../data/models/service-plan.model';
import { TransportationBenefit } from '../../../data/models/transportation-benefit.model';
import * as clientSlice from '../../../data/client.slice';
import * as reservationSlice from '../../../data/reservation.slice';
import { DateTime } from 'luxon';
import { Passenger } from '../../../data/models/passenger.model';
import dateUtils from '../../../services/DateUtils';

const PREFIX = 'PassengerBenefits';

const classes = {
  container: `${PREFIX}-container`,
  details: `${PREFIX}-details`,
  header: `${PREFIX}-header`,
  benefits: `${PREFIX}-benefits`,
  benefitRow: `${PREFIX}-benefitRow`,
  noEligibility: `${PREFIX}-noEligibility`,
  benefitHeadingRow: `${PREFIX}-benefitHeadingRow`,
  transportationBenefits: `${PREFIX}-transportationBenefits`,
  benefitItemRow: `${PREFIX}-benefitItemRow`,
  benefitItemStats: `${PREFIX}-benefitItemStats`,
  benefitTitle: `${PREFIX}-benefitTitle`,
  benefitSubTitle: `${PREFIX}-benefitSubTitle`,
  benefitItem: `${PREFIX}-benefitItem`,
  benefitItemTitle: `${PREFIX}-benefitItemTitle`,
  benefitItemSubTitle: `${PREFIX}-benefitItemSubTitle`,
  benefitItemHeading: `${PREFIX}-benefitItemHeading`,
  benefitItemValue: `${PREFIX}-benefitItemValue`,
  spacer: `${PREFIX}-spacer`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    gap: theme.spacing(4),
  },

  [`& .${classes.details}`]: {
    padding: theme.spacing(3, 4),
    gap: theme.spacing(2),
  },

  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    paddingBottom: theme.spacing(1),
  },

  [`& .${classes.benefits}`]: {
    paddingTop: 0,
  },

  [`& .${classes.noEligibility}`]: {
    fontWeight: 600,
    fontSize: 18,
    lineHeight: '150%',
    letterSpacing: '0.15px',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    color: theme.palette.error.light,
  },

  [`& .${classes.benefitRow}`]: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    '&:first-of-type': {
      paddingTop: 0,
    },
  },

  [`& .${classes.benefitHeadingRow}`]: {
    paddingTop: theme.spacing(1),
  },

  [`& .${classes.transportationBenefits}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.benefitTitle}`]: {
    lineHeight: '150%',
    fontSize: 18,
  },

  [`& .${classes.benefitSubTitle}`]: {
    fontSize: 12,
    lineHeight: '166%',
    letterSpacing: '0.4px',
    fontWeight: 400,
    color: theme.palette.lightTextSecondary.main,
  },

  [`& .${classes.benefitItemRow}`]: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    '&:first-of-type': {
      paddingTop: 0,
    },
  },

  [`& .${classes.benefitItemStats}`]: {
    display: 'flex',
    flexDirection: 'row',
    paddingTop: theme.spacing(1),
  },

  [`& .${classes.benefitItem}`]: {
    flexGrow: 1,
    flexBasis: 0,
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 125,
  },

  [`& .${classes.benefitItemTitle}`]: {
    marginTop: 0,
    fontWeight: 600,
  },

  [`& .${classes.benefitItemHeading}`]: {
    lineHeight: '100%',
  },

  [`& .error`]: {
    color: theme.palette.error.light,
  },

  [`& .${classes.spacer}`]: {
    flex: 1,
  },
}));

interface PassengerBenefitsProps {
  appointmentDateTime?: DateTime;
  servicePlans: ServicePlan[];
  passenger?: Passenger | null;
  isMemberPortal?: boolean;
}

type TransportationBenefitWithTripCount = TransportationBenefit & {
  effectiveStartDate: string;
  tripCount?: number;
  maxTripCount?: number;
};

type ServicePlanWithBenefits = ServicePlan & {
  transportationBenefits?: TransportationBenefitWithTripCount[];
};

export default function PassengerBenefits({
  appointmentDateTime,
  servicePlans,
  passenger,
  isMemberPortal = false,
}: PassengerBenefitsProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const appointmentDate =
    appointmentDateTime?.toISODate() || DateTime.now().toISODate();
  const clientTransportationBenefits = useAppSelector(
    clientSlice.selectTransportationBenefits,
  );
  const loadingServicePlans = useAppSelector(
    clientSlice.selectLoadingPassengerClientData,
  );
  const loadedServicePlans = useAppSelector(
    clientSlice.selectLoadedPassengerClientData,
  );
  const loadingBenefits = useAppSelector(
    clientSlice.selectLoadingTransportationBenefits,
  );
  const loadedBenefits = useAppSelector(
    clientSlice.selectLoadedTransportationBenefits,
  );
  const loadingBenefitsSummary = useAppSelector(
    reservationSlice.selectLoadingBenefitSummary,
  );
  const loadedBenefitsSummary = useAppSelector(
    reservationSlice.selectLoadedBenefitSummary,
  );
  const loadingData =
    loadingBenefits || loadingBenefitsSummary || loadingServicePlans;
  const loadedData =
    loadedServicePlans && loadedBenefits && loadedBenefitsSummary;

  const benefitSummary = useAppSelector(reservationSlice.selectBenefitSummary);
  const availableServicePlans: ServicePlan[] = useMemo(
    () =>
      servicePlans.filter(
        ({ effectiveEndDate }) =>
          !effectiveEndDate || appointmentDate <= effectiveEndDate,
      ),
    [appointmentDate, servicePlans],
  );

  const servicePlansWithBenefits: ServicePlanWithBenefits[] = useMemo(
    () =>
      availableServicePlans
        .map((servicePlan) => {
          const matchingTransportationBenefits =
            clientTransportationBenefits?.filter((item) =>
              servicePlan?.transportationBenefitUUIDs?.includes(
                item?.transportationBenefitUUID || '',
              ),
            );
          // Find the matching benefit in the benefit summary and store the use count with
          // the transportation benefit
          const mappedTransporationBenefits: Partial<TransportationBenefitWithTripCount>[] =
            matchingTransportationBenefits?.map((transportationBenefit) => {
              const maxTripCount =
                transportationBenefit?.functionParameters?.find(
                  (param) => param.name === 'maxTripCount',
                )?.value;
              const transportationBenefitUUID =
                transportationBenefit.transportationBenefitUUID;
              if (transportationBenefitUUID) {
                return {
                  ...transportationBenefit,
                  ...dateUtils.getIntersectingDateRange(
                    transportationBenefit,
                    servicePlan,
                  ),
                  maxTripCount,
                  tripCount:
                    benefitSummary[servicePlan.servicePlanUUID]?.[
                      transportationBenefitUUID!
                    ],
                };
              }
              return {
                ...transportationBenefit,
                maxTripCount,
              };
            });
          // Filter benefits that will be active on the appointment date or a future date
          const filteredTransportationBenefits =
            mappedTransporationBenefits.filter(
              (benefit): benefit is TransportationBenefitWithTripCount => {
                // Common date range can result undefined effective start and end dates
                // which means service plan and the transportation benefit does not overlap
                return (
                  !!benefit.effectiveStartDate &&
                  (!benefit.effectiveEndDate ||
                    appointmentDate <= benefit.effectiveEndDate)
                );
              },
            );

          return {
            ...servicePlan,
            transportationBenefits: filteredTransportationBenefits,
          };
        })
        .filter((servicePlan) => servicePlan.transportationBenefits.length),
    [availableServicePlans, benefitSummary, clientTransportationBenefits],
  );

  React.useEffect(() => {
    if (availableServicePlans.length > 0) {
      dispatch(
        clientSlice.fetchTransportationBenefitsAsync(
          availableServicePlans[0]?.clientBC || '',
        ),
      );
    }
  }, [availableServicePlans]);

  React.useEffect(() => {
    if (availableServicePlans.length > 0) {
      if (passenger) {
        const servicePlanUUIDs = availableServicePlans.map(
          (servicePlan) => servicePlan.servicePlanUUID,
        );
        dispatch(
          reservationSlice.fetchPassengerBenefitSummaryAsync(
            passenger.passengerUUID || '',
            servicePlanUUIDs,
            appointmentDate,
          ),
        );
      }
    }
  }, [appointmentDate, availableServicePlans, passenger]);

  const [eligibilityExpired, setEligibilityExpired] = React.useState(false);

  React.useEffect(() => {
    const hasAnyBenefitEligibility = servicePlansWithBenefits.some(
      ({ transportationBenefits }) =>
        transportationBenefits?.some(
          ({ effectiveStartDate, effectiveEndDate, maxTripCount, tripCount }) =>
            effectiveStartDate <= appointmentDate &&
            (!effectiveEndDate || appointmentDate <= effectiveEndDate) &&
            (!(tripCount && maxTripCount) || tripCount < maxTripCount),
        ),
    );
    setEligibilityExpired(!hasAnyBenefitEligibility);
  }, [servicePlansWithBenefits]);

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.details}>
        <Box className={classes.header}>
          <Typography variant="h3">{t('passenger.benefits')}</Typography>
        </Box>
        {loadingData && (
          <Box className={classes.container} sx={{ textAlign: 'center' }}>
            <CircularProgress sx={{ textAlign: 'center' }} />
          </Box>
        )}
        {!loadingData &&
          !loadedData &&
          loadedServicePlans &&
          servicePlansWithBenefits.length < 1 && (
            <>
              <Divider />
              <Typography className={classes.noEligibility}>
                {t('passenger.noEligibility')}
              </Typography>
            </>
          )}
        {loadedData && (
          <>
            <Divider />
            {(eligibilityExpired || servicePlansWithBenefits.length < 1) && (
              <Typography color="error" className={classes.noEligibility}>
                {t('passenger.noEligibility')}
              </Typography>
            )}
            <Box className={classes.benefits}>
              {servicePlansWithBenefits.map((servicePlan, key) => (
                <Box key={key} className={classes.benefitRow}>
                  <Box className={classes.benefitHeadingRow}>
                    <Typography
                      variant="subtitle1"
                      className={classes.benefitTitle}>
                      {servicePlan?.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={classes.benefitSubTitle}>
                      {!servicePlan?.effectiveEndDate && (
                        <>{t('passenger.starts') + ': '} </>
                      )}
                      {dateUtils.formatDate(
                        DateTime.fromISO(servicePlan?.effectiveStartDate),
                      )}
                      {servicePlan?.effectiveEndDate && (
                        <>
                          {' - '}
                          {dateUtils.formatDate(
                            DateTime.fromISO(servicePlan?.effectiveEndDate),
                          )}
                        </>
                      )}
                    </Typography>
                  </Box>
                  <Box className={classes.transportationBenefits}>
                    {servicePlan.transportationBenefits?.map((item, key) => (
                      <React.Fragment key={key}>
                        <Box className={classes.benefitItemRow}>
                          <Typography
                            variant="body2"
                            className={classes.benefitItemTitle}>
                            {item?.name || ''}
                          </Typography>
                          {(item?.effectiveStartDate ||
                            item?.effectiveEndDate) && (
                            <Typography
                              variant="body2"
                              className={classes.benefitSubTitle}>
                              {!item?.effectiveEndDate && (
                                <>{t('passenger.starts') + ': '} </>
                              )}
                              {dateUtils.formatDate(
                                DateTime.fromISO(item?.effectiveStartDate),
                              )}
                              {item?.effectiveEndDate && (
                                <>
                                  {' - '}{' '}
                                  {dateUtils.formatDate(
                                    DateTime.fromISO(item?.effectiveEndDate),
                                  )}
                                </>
                              )}
                            </Typography>
                          )}
                          <Box className={classes.benefitItemStats}>
                            {!isMemberPortal && (
                              <Box className={classes.benefitItem}>
                                <Typography
                                  variant="overline"
                                  className={classes.benefitItemHeading}>
                                  {t('passenger.total')}
                                </Typography>
                                <Typography
                                  variant="h3"
                                  component="span"
                                  className={classes.benefitItemValue}>
                                  {item.maxTripCount ??
                                    t('passenger.unlimited')}
                                </Typography>
                              </Box>
                            )}
                            <Box className={classes.benefitItem}>
                              <Typography
                                variant="overline"
                                className={classes.benefitItemHeading}>
                                {t('passenger.available')}
                              </Typography>
                              <Typography
                                variant="h3"
                                component="span"
                                className={`${classes.benefitItemValue} ${item.maxTripCount && item.maxTripCount - (item.tripCount || 0) == 0 ? 'error' : ''}`}>
                                {typeof item.maxTripCount === 'number'
                                  ? item.maxTripCount - (item.tripCount || 0)
                                  : t('passenger.unlimited')}
                              </Typography>
                            </Box>
                            <Box className={classes.benefitItem}>
                              <Typography
                                variant="overline"
                                className={classes.benefitItemHeading}>
                                {t('passenger.used')}
                              </Typography>
                              <Typography
                                variant="h3"
                                component="span"
                                className={classes.benefitItemValue}>
                                {item.tripCount || 0}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Paper>
    </StyledBox>
  );
}
